// Demo Making Tax Digital. The demo books are Irish, so this only shows when
// the demo is switched to UK books: setDemoJurisdiction('UK') in tests, or
// localStorage "elysian.demo.jurisdiction" = "UK" in the browser. HMRC itself
// is pretended: connecting succeeds at once and returns get a made-up receipt.
import type { HmrcStatus, LedgerSource, MtdObligation, MtdReceipt, MtdVatReturn } from './source';

type MtdSource = Pick<LedgerSource, 'hmrcStatus' | 'connectHmrc' | 'disconnectHmrc' | 'mtdObligations' | 'submitMtdReturn' | 'mtdReceipt'>;

const delay = <T,>(v: T, ms = 80): Promise<T> => new Promise((r) => setTimeout(() => r(structuredClone(v)), ms));
const fail = (detail: string) => Promise.reject(new Error(detail));

const DEMO_TODAY = '2026-10-11';
const VRN = '123456789';

let jurisdiction: 'IE' | 'UK' = (() => {
  try {
    return localStorage.getItem('elysian.demo.jurisdiction') === 'UK' ? 'UK' : 'IE';
  } catch {
    return 'IE';
  }
})();

/** Which books the demo pretends to be. */
export function demoJurisdiction(): 'IE' | 'UK' {
  return jurisdiction;
}

export function setDemoJurisdiction(j: 'IE' | 'UK') {
  jurisdiction = j;
}

const box = (sales: number, reclaimed: number, salesEx: number, purchasesEx: number): MtdVatReturn => ({
  vatDueSales: sales, vatDueAcquisitions: 0, totalVatDue: sales, vatReclaimedCurrPeriod: reclaimed,
  netVatDue: Math.round(Math.abs(sales - reclaimed) * 100) / 100, totalValueSalesExVAT: salesEx, totalValuePurchasesExVAT: purchasesEx,
  totalValueGoodsSuppliedExVAT: 0, totalAcquisitionsExVAT: 0,
});

interface Stored extends MtdObligation { receipt?: MtdReceipt }

let connected: { at: string; by: string } | null = null;
let obligations: Stored[] = [];
let bundle = 0;

function seed() {
  connected = null;
  bundle = 0;
  const q2 = box(9184.6, 3302.18, 45923, 16510);
  obligations = [
    { period_key: '26A4', start: '2026-10-01', end: '2026-12-31', due: '2027-02-07', status: 'open', figures: null },
    { period_key: '26A3', start: '2026-07-01', end: '2026-09-30', due: '2026-11-07', status: 'due', figures: box(8412.3, 2957.42, 42061, 14787) },
    { period_key: '26A2', start: '2026-04-01', end: '2026-06-30', due: '2026-08-07', status: 'fulfilled', received: '2026-07-28', figures: null,
      form_bundle_number: '092600418812', submitted_at: '2026-07-28T15:02:11Z',
      receipt: { period_key: '26A2', vrn: VRN, from: '2026-04-01', to: '2026-06-30', submitted: { ...q2, periodKey: '26A2', finalised: true },
        receipt: { processingDate: '2026-07-28T15:02:11.000Z', formBundleNumber: '092600418812', paymentIndicator: 'DD', chargeRefNumber: 'XM002610011594' },
        submitted_by: 'ledger:user:brian', submitted_at: '2026-07-28T15:02:11Z' } },
    { period_key: '26A1', start: '2026-01-01', end: '2026-03-31', due: '2026-05-07', status: 'fulfilled', received: '2026-05-02', figures: null },
  ];
}
seed();

export function resetDemoMtd() {
  seed();
}

const view = ({ receipt: _r, ...o }: Stored): MtdObligation => o;

function status(): HmrcStatus {
  const uk = jurisdiction === 'UK';
  return {
    available: uk, vrn: uk ? VRN : null, connected: uk && !!connected, connected_vrn: connected ? VRN : null,
    connected_by: connected?.by ?? null, connected_at: connected?.at ?? null,
    expires_at: connected ? '2028-04-11T09:00:00Z' : null, last_used_at: null, last_error: null,
    problem: uk ? null : 'Making Tax Digital is for UK VAT', sandbox: true, configured: true,
  };
}

export const demoMtd: MtdSource = {
  hmrcStatus: () => delay(status()),
  connectHmrc: () => {
    if (jurisdiction !== 'UK') return fail('Making Tax Digital is for UK VAT.');
    connected = { at: `${DEMO_TODAY}T09:00:00Z`, by: 'ledger:user:brian' };
    // A real connection goes to HMRC's sign-in page; the demo comes straight back as HMRC would.
    return delay({ authorise_url: '/vat?hmrc=connected' });
  },
  disconnectHmrc: () => {
    connected = null;
    return delay(undefined);
  },
  mtdObligations: () => {
    if (!connected) return fail('Connect to HMRC first.');
    return delay({ vrn: VRN, sandbox: true, obligations: obligations.map(view) });
  },
  submitMtdReturn: (periodKey, from, to) => {
    if (!connected) return fail('Connect to HMRC first.');
    const o = obligations.find((x) => x.period_key === periodKey);
    if (!o || o.start !== from || o.end !== to) return fail('HMRC doesn’t recognise that period. Refresh the list of returns and try again.');
    if (o.status === 'fulfilled') return fail('HMRC already has a return for this period. Check it in your HMRC online account.');
    if (o.end >= DEMO_TODAY || !o.figures) return fail(`The period has not ended yet (${o.end}); HMRC takes the return from the day after.`);
    const formBundleNumber = `0926${String(10_481_220 + ++bundle).padStart(8, '0')}`;
    const at = `${DEMO_TODAY}T10:12:00Z`;
    const receipt: MtdReceipt = {
      period_key: periodKey, vrn: VRN, from, to, submitted: { ...o.figures, periodKey, finalised: true },
      receipt: { processingDate: `${DEMO_TODAY}T10:12:00.000Z`, formBundleNumber,
        paymentIndicator: o.figures.vatDueSales >= o.figures.vatReclaimedCurrPeriod ? 'DD' : 'BANK', chargeRefNumber: 'XM002610011602' },
      submitted_by: 'ledger:user:brian', submitted_at: at,
    };
    obligations = obligations.map((x) => (x.period_key === periodKey
      ? { ...x, status: 'fulfilled', received: DEMO_TODAY, figures: null, form_bundle_number: formBundleNumber, submitted_at: at, receipt } : x));
    return delay(receipt, 200);
  },
  mtdReceipt: (periodKey) => {
    const r = obligations.find((x) => x.period_key === periodKey)?.receipt;
    return r ? delay(r) : fail('Nothing was sent for that period from Elysian.');
  },
};
